import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectNodes, selectEdges, updateNode, bulkUpdateNodes } from '../../../redux/slices/adsOptimizationSlice';
import { calculateNodeSchema } from '../utils/SchemaDefinitions';

// Helper to check if two schemas are equal
const isSchemaEqual = (schema1, schema2) => {
    if (schema1 === schema2) return true;
    if (!schema1 || !schema2) return false;
    if (schema1.length !== schema2.length) return false;
    
    // Simple comparison of column names and types
    for (let i = 0; i < schema1.length; i++) {
        if (schema1[i].name !== schema2[i].name || schema1[i].type !== schema2[i].type) {
            return false;
        }
    }
    return true;
};

// Helper to check if nodes have meaningful changes (not just position)
const hasNonPositionChanges = (prevNodes, currentNodes, prevEdges, currentEdges) => {
    // Quick check: different lengths
    if (prevNodes.length !== currentNodes.length) return true;
    if (prevEdges.length !== currentEdges.length) return true;
    
    // Check edges - any change in connections is meaningful
    const edgesChanged = currentEdges.some((edge, i) => {
        const prevEdge = prevEdges[i];
        if (!prevEdge) return true;
        return edge.source !== prevEdge.source || 
               edge.target !== prevEdge.target ||
               edge.sourceHandle !== prevEdge.sourceHandle ||
               edge.targetHandle !== prevEdge.targetHandle;
    });
    if (edgesChanged) return true;
    
    // Check nodes - only care about data/config changes, not position
    const prevNodeMap = new Map(prevNodes.map(n => [n.id, n]));
    for (const node of currentNodes) {
        const prevNode = prevNodeMap.get(node.id);
        if (!prevNode) return true; // New node
        
        // Compare data reference - if data object is same reference, no changes
        if (node.data !== prevNode.data) return true;
    }
    
    return false;
};

const useSchemaPropagation = () => {
    const dispatch = useDispatch();
    const nodes = useSelector(selectNodes);
    const edges = useSelector(selectEdges);
    
    // Track previous state to detect position-only changes
    const prevNodesRef = useRef(nodes);
    const prevEdgesRef = useRef(edges);
    const lastSchemasRef = useRef({});
    const isPropagationNeeded = useRef(false);

    // Check if we need to run propagation (skip position-only changes)
    useEffect(() => {
        const prevNodes = prevNodesRef.current;
        const prevEdges = prevEdgesRef.current;
        
        if (hasNonPositionChanges(prevNodes, nodes, prevEdges, edges)) {
            isPropagationNeeded.current = true;
        }
        
        prevNodesRef.current = nodes;
        prevEdgesRef.current = edges;
    }, [nodes, edges]);

    const propagateSchemas = useCallback(() => {
        // Skip if only position changed
        if (!isPropagationNeeded.current) {
            return;
        }
        isPropagationNeeded.current = false;
        
        if (!nodes.length) return;

        // 1. Build Adjacency List for traversal
        const adjacencyList = {};
        const inDegree = {};
        
        nodes.forEach(node => {
            adjacencyList[node.id] = [];
            inDegree[node.id] = 0;
        });

        edges.forEach(edge => {
            if (adjacencyList[edge.source]) {
                adjacencyList[edge.source].push(edge.target);
            }
            if (inDegree[edge.target] !== undefined) {
                inDegree[edge.target]++;
            }
        });

        // 2. Topological Sort (Kahn's Algorithm)
        const queue = [];
        nodes.forEach(node => {
            if (inDegree[node.id] === 0) {
                queue.push(node.id);
            }
        });

        const sortedNodeIds = [];
        while (queue.length > 0) {
            const nodeId = queue.shift();
            sortedNodeIds.push(nodeId);

            if (adjacencyList[nodeId]) {
                adjacencyList[nodeId].forEach(neighborId => {
                    inDegree[neighborId]--;
                    if (inDegree[neighborId] === 0) {
                        queue.push(neighborId);
                    }
                });
            }
        }

        // Handle cycles (nodes not in sortedNodeIds) - just process them at the end or ignore
        // For now, we append remaining nodes
        nodes.forEach(node => {
            if (!sortedNodeIds.includes(node.id)) {
                sortedNodeIds.push(node.id);
            }
        });

        // 3. Calculate Schemas
        const nodeMap = nodes.reduce((acc, node) => {
            acc[node.id] = node;
            return acc;
        }, {});

        const newSchemas = {}; // nodeId -> { inputSchema, outputSchema }
        let hasChanges = false;
        const updatesToDispatch = [];

        sortedNodeIds.forEach(nodeId => {
            const node = nodeMap[nodeId];
            if (!node) return;

            // Find input schema from incoming edges
            const incomingEdges = edges.filter(e => e.target === nodeId);
            let inputSchema = [];
            const inputSchemasByHandle = {};
            const allInputSchemas = [];

            if (incomingEdges.length > 0) {
                // For now, we take the schema from the first source. 
                // In complex scenarios (Join/Union), we might need to merge schemas.
                // But for linear flows, taking the first one is fine.
                const sourceNodeId = incomingEdges[0].source;
                // Use the calculated output schema of the source node
                inputSchema = newSchemas[sourceNodeId]?.outputSchema || [];

                // Collect schemas by handle for multi-input nodes (like Join)
                incomingEdges.forEach(edge => {
                    const sId = edge.source;
                    const sSchema = newSchemas[sId]?.outputSchema || [];
                    allInputSchemas.push(sSchema);
                    if (edge.targetHandle) {
                        inputSchemasByHandle[edge.targetHandle] = sSchema;
                    }
                });
            }

            // Calculate output schema
            const nodeType = node.data?.nodeType || node.data?.type || node.type;
            const config = node.data?.config || {};

            // SPECIAL LOGIC: Auto-configure Table Node from Excel/CSV Source
            // This ensures that when an Excel file is uploaded, the connected Table node
            // automatically picks up the file and the first sheet.
            if (nodeType === 'table' && incomingEdges.length > 0) {
                const incomingEdge = incomingEdges[0];
                const sourceNode = nodeMap[incomingEdge.source];
                const sourceType = sourceNode?.data?.nodeType || sourceNode?.data?.type;

                if (sourceNode && (sourceType === 'excel_input' || sourceType === 'csv_input')) {
                    const sourceConfig = sourceNode.data?.config || {};
                    const sourceSheets = sourceNode.data?.sheets || sourceConfig.sheets || [];
                    
                    // Check if we need to auto-configure
                    // 1. Source ID mismatch (connected to a new node)
                    // 2. Sheet mismatch (selected sheet not in source - e.g. file changed)
                    // 3. Not configured yet
                    
                    const currentSheet = config.selectedSheet;
                    const isSourceMatch = config.sourceNodeId === sourceNode.id;
                    const isSheetValid = currentSheet && sourceSheets.some(s => s.name === currentSheet);
                    
                    // If source has data but table is not correctly configured for it
                    if (sourceSheets.length > 0 && (!isSourceMatch || !isSheetValid)) {
                        console.log(`Auto-configuring Table Node ${nodeId} from Source ${sourceNode.id}`);
                        
                        // Auto-configure to first sheet
                        const firstSheet = sourceSheets[0];
                        const baseFileName = sourceNode.data?.fileName || sourceConfig.fileName || 'data';
                        const cleanFileName = baseFileName.replace(/\.(xlsx|xls|csv)$/i, '');
                        const tableName = firstSheet.name === 'CSV Data' ? cleanFileName : `${cleanFileName}_${firstSheet.name}`;
                        
                        const newConfig = {
                            ...config,
                            sourceNodeId: sourceNode.id,
                            selectedSheet: firstSheet.name,
                            selectedSchema: firstSheet.schema || [], // Use schema from source
                            headers: firstSheet.headers || [],
                            totalRows: firstSheet.totalRows || 0,
                            tableName: tableName
                        };
                        
                        // Add to updates
                        hasChanges = true;
                        updatesToDispatch.push({
                            nodeId,
                            newData: {
                                ...node.data,
                                customName: tableName, // Also update node label/name
                                label: tableName,
                                config: newConfig
                            }
                        });
                        
                        // Update local config variable for schema calculation below
                        // (though output schema for Table node usually depends on its config)
                        // config = newConfig; // We can't easily reassign const, but we can use newConfig in calculation if needed
                    }
                }
            }
            
            const outputSchema = calculateNodeSchema(nodeType, config, inputSchema, inputSchemasByHandle, allInputSchemas);

            newSchemas[nodeId] = { inputSchema, outputSchema };

            // Check if we need to update the node in Redux
            // We compare with the CURRENT data in the node
            const currentInputSchema = node.data?.inputSchema;
            const currentOutputSchema = node.data?.outputSchema;

            if (!isSchemaEqual(currentInputSchema, inputSchema) || 
                !isSchemaEqual(currentOutputSchema, outputSchema)) {
                
                hasChanges = true;
                updatesToDispatch.push({
                    nodeId,
                    newData: {
                        ...node.data,
                        inputSchema,
                        outputSchema
                    }
                });
            }
        });

        // 4. Dispatch updates - use bulkUpdateNodes for efficiency
        if (hasChanges && updatesToDispatch.length > 0) {
            console.log(`Schema propagation: Updating ${updatesToDispatch.length} nodes...`);
            dispatch(bulkUpdateNodes(updatesToDispatch));
        }

    }, [nodes, edges, dispatch]);

    // Debounce the propagation - only run when meaningful changes detected
    useEffect(() => {
        // Early exit if no propagation needed
        if (!isPropagationNeeded.current) {
            return;
        }
        
        const handler = setTimeout(() => {
            propagateSchemas();
        }, 300); // Reduced debounce since we filter position-only changes

        return () => clearTimeout(handler);
    }, [propagateSchemas, nodes, edges]); // Include nodes/edges to trigger check
};

export default useSchemaPropagation;
